import { useLayout } from '../../../Framework/Providers/LayoutProvider';
import { ButtonSize, ToggleType } from '../../../Framework/Data/Enums/Generic';
import { ColourMod, FlexAlign, FlexGap, FlexJustify } from '../../../Framework/Data/Enums/LayoutEnums';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faBomb,
	faClipboard,
	faCopy,
	faExternalLink,
	faPage,
	faPaperPlane,
	faRectangleHistory,
	faTrash,
	faWavePulse,
} from '@fortawesome/pro-solid-svg-icons';
import Button from '../../../Framework/Components/Buttons/Button';
import {
	SendsPageDataType,
	updateSendsPageActiveStatus,
	useChangeSendsStatus,
	useInfiniteSendsPage,
} from '../../../Api/StandaloneSendsEndpoints';
import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import ToggleSwitch from '../../../Framework/Components/Forms/Toggle/ToggleSwitch';
import FullWidthSidebar from '../../../Framework/Components/PageLayouts/FullWidthSidebar';
import SendsAdminNavigation from '../../../Components/Sends/SendsAdminNavigation';
import BasicPageHeader from '../../../Framework/Components/LayoutScaffold/BasicPageHeader';
import StackHorizontal from '../../../Framework/Components/Wrappers/StackHorizontal';
import ConfirmDeleteSendsPageModal from '../../../Components/Sends/Modals/ConfirmDeleteSendsPageModal';
import { GetInitials, ParseURL } from '../../../Framework/Constants/Helpers';
import StackVertical from '../../../Framework/Components/Wrappers/StackVertical';
import InfiniteLoadTrigger from '../../../Framework/Components/InfiniteLoadTrigger';
import { GetItemCounts } from '../../../Framework/Constants/DataHelpers';
import { PaginatedResponseType } from '../../../Types/Response/PaginatedResponseType';
import DropdownMenu from '../../../Framework/Components/DropdownMenus/DropdownMenu';
import { faCogs } from '@fortawesome/pro-light-svg-icons';
import ItemMenuEntry from '../../../Framework/Components/Navigation/ItemMenuEntry';
import { useToasts } from '../../../Framework/Providers/ToastsProvider';

const SendsPagesOverview = () => {
	const navigate = useNavigate();

	const { isMedium, isLarge, isXL } = useLayout();
	const { addToast } = useToasts();

	const {
		status,
		data,
		error,
		isFetchingNextPage,
		fetchNextPage,
		hasNextPage,
	}: {
		status: any;
		data: PaginatedResponseType | any;
		error: any;
		isFetching: any;
		isFetchingNextPage: any;
		fetchNextPage: any;
		hasNextPage?: any;
	} = useInfiniteSendsPage();

	const [activeSendsPage, setActiveSendsPage] = useState<any>({ isOpen: false, pageData: null });
	const [itemCounts, setItemCounts] = useState<any>({ loaded: 0, total: 0 });

	const updateSendsStatus = useChangeSendsStatus();

	const sendsPagesRef = useRef<any>();

	const HandleTemplateStatus = async (pageId: number, active: any) => {
		await updateSendsPageActiveStatus(pageId, active).then((resp) => {
			updateSendsStatus.mutate({
				newData: active,
				id: pageId,
			});
		});
	};

	const ShowDeleteConfirmation = (page: SendsPageDataType) => {
		setActiveSendsPage({ pageData: page, isOpen: true });
	};

	const HandleOpen = async (slug: string) => {
		window.open(slug, '_blank');
	};

	const HandleCopyUrl = async (slug: string) => {
		addToast('Copied to Clipboard', <FontAwesomeIcon icon={faClipboard} />);
		await navigator.clipboard.writeText(slug);
	};

	const ItemPlaceholder = () => {
		return (
			<div
				className={'content-loader content-fader'}
				style={{
					display: 'grid',
					gap: 'var(--sm-unit)',
					margin: '0 var(--md-unit)',
					gridTemplateColumns: isMedium ? 'repeat(3, 1fr) 5em' : '1fr 5em',
				}}
			>
				{isMedium ? (
					<>
						<div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
						<div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
						<div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
						<div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
					</>
				) : (
					<>
						<div style={{ width: 'auto', height: '2em' }} />
						<div style={{ width: 'auto', height: '2em' }} />
					</>
				)}
			</div>
		);
	};

	const PageActions = () => {
		return (
			<Button
				colour={ColourMod.Primary}
				onClick={() => navigate('/sends-admin/create')}
				squared={true}
				title={'New Profile'}
				icon={<FontAwesomeIcon icon={faPage} />}
			/>
		);
	};

	useEffect(() => {
		if (data) {
			setItemCounts(GetItemCounts(data));
		}
	}, [data]);

	useEffect(() => {
		document.title = 'Profiles Admin';
	}, []);

	return (
		<FullWidthSidebar
			ref={sendsPagesRef}
			navigationComponent={
				<>
					<SendsAdminNavigation
						title={'Profiles'}
						addonContent={
							<div className={'section-navigation-header-collapsed-addon'}>
								{!isLarge && <PageActions />}
							</div>
						}
					/>
				</>
			}
			extraContentClass={`flex-column `}
		>
			{isLarge && (
				<BasicPageHeader title={'Profiles'}>
					<PageActions />
				</BasicPageHeader>
			)}

			{isMedium && (
				<div
					className={'item-row item-row-header pl-md pr-md'}
					style={{ gridTemplateColumns: 'repeat(3, 1fr) 5em' }}
				>
					<div className={'sm-sub-heading'}>Name</div>
					<div className={'sm-sub-heading'}>Users</div>
					<div className={'sm-sub-heading'}>Actions</div>
					<div className={'sm-sub-heading'}>Active</div>
				</div>
			)}

			{status === 'loading' ? (
				<div className={'content-fader'}>
					<ItemPlaceholder />
					<ItemPlaceholder />
					<ItemPlaceholder />
					<ItemPlaceholder />
					<ItemPlaceholder />
					<ItemPlaceholder />
					<ItemPlaceholder />
				</div>
			) : status === 'error' ? (
				<StackVertical
					gap={FlexGap.ExtraSmall}
					align={FlexAlign.Center}
					justify={FlexJustify.Center}
					extraClass={'m-xxl p-xxl text-center'}
				>
					<FontAwesomeIcon icon={faBomb} size={'3x'} />
					<h6 className={'sub-heading mt-sm mb-xs'}>Profiles Error</h6>
					<p>{error.message}</p>
				</StackVertical>
			) : (
				<>
					{data &&
						data.pages.map((group: any, groupIndex: number) =>
							group.data.map((pageEntry: SendsPageDataType, index: number) => {
								return (
									<NavLink
										to={`/sends-admin/edit/${pageEntry.id}`}
										key={pageEntry.id}
										className={'item-row pl-md pr-md'}
										style={{ gridTemplateColumns: isMedium ? 'repeat(3, 1fr) 5em' : '1fr 5em' }}
									>
										{isMedium ? (
											<>
												<div className={'flex-row align-center flex-gap-xs'}>
													<div className={'flex-column flex-gap-xs'}>{pageEntry.title}</div>
												</div>
												<StackHorizontal gap={FlexGap.ExtraSmall} wrap>
													{pageEntry.users.map((user, index) => {
														if (pageEntry.users.length <= 3 && isXL) {
															return (
																<div
																	className={'initials-badge'}
																	key={`pageUser${pageEntry.id}${index}`}
																	title={user.name}
																>
																	{user.name as string}
																</div>
															);
														}

														return user.name && index < 7 ? (
															<div
																className={'initials-badge'}
																key={`pageUser${pageEntry.id}${index}`}
																title={user.name}
															>
																{GetInitials(user.name as string)}
															</div>
														) : (
															<></>
														);
													})}

													{pageEntry.users.length > 7 && (
														<div className={'initials-badge'}>
															+{pageEntry.users.length - 7} More
														</div>
													)}
												</StackHorizontal>
												<StackHorizontal gap={FlexGap.ExtraSmall}>
													<Button
														onClick={(e) => [
															e.preventDefault(),
															HandleOpen(pageEntry.liveUrl ? pageEntry.liveUrl : ''),
														]}
														size={ButtonSize.Small}
														colour={ColourMod.Secondary}
														title={'Open'}
														iconOnly={!isXL}
														icon={
															<FontAwesomeIcon icon={faExternalLink} fixedWidth={true} />
														}
														squared={true}
													/>
													<Button
														onClick={(e) => [
															e.preventDefault(),
															HandleCopyUrl(pageEntry.liveUrl ? pageEntry.liveUrl : ''),
														]}
														size={ButtonSize.Small}
														colour={ColourMod.Secondary}
														title={'Copy URL'}
														iconOnly={!isXL}
														icon={<FontAwesomeIcon icon={faCopy} fixedWidth={true} />}
														squared={true}
													/>
													<Button
														onClick={(e) => [
															e.preventDefault(),
															ShowDeleteConfirmation(pageEntry),
														]}
														size={ButtonSize.Small}
														colour={ColourMod.Alert}
														title={'Delete'}
														iconOnly={!isXL}
														icon={<FontAwesomeIcon icon={faTrash} fixedWidth={true} />}
														squared={true}
													/>
												</StackHorizontal>
												<div onClick={(e) => e.stopPropagation()}>
													<ToggleSwitch
														type={ToggleType.Switch}
														hideLabel={true}
														name={`sendsPageEntry${pageEntry.id}`}
														label={'Active'}
														checked={pageEntry.isActive}
														onChange={(e: any) =>
															HandleTemplateStatus(
																pageEntry.id ? pageEntry.id : -1,
																e.currentTarget.checked
															)
														}
													/>
												</div>
											</>
										) : (
											<>
												<StackVertical>
													<div className={'flex-row align-center flex-gap-xs'}>
														<div className={'flex-column flex-gap-xs'}>
															<h5 className={'m-0'}>{pageEntry.title}</h5>
														</div>
													</div>
													<StackHorizontal gap={FlexGap.ExtraSmall} wrap>
														{pageEntry.users.map((user, index) => {
															if (pageEntry.users.length <= 3 && isXL) {
																return (
																	<div
																		className={'initials-badge'}
																		key={`pageUser${pageEntry.id}${index}`}
																		title={user.name}
																	>
																		{user.name as string}
																	</div>
																);
															}

															return user.name ? (
																<div
																	className={'initials-badge'}
																	key={`pageUser${pageEntry.id}${index}`}
																	title={user.name}
																>
																	{GetInitials(user.name as string)}
																</div>
															) : (
																<></>
															);
														})}
													</StackHorizontal>
												</StackVertical>

												<div onClick={(e) => e.preventDefault()}>
													<Button
														title={'Actions'}
														squared={true}
														extraClass={`page-actions-${index}`}
														icon={<FontAwesomeIcon icon={faCogs} fixedWidth={true} />}
														iconOnly={true}
														onClick={() => null}
													/>
													<DropdownMenu target={[`page-actions-${index}`]}>
														<ItemMenuEntry
															title={'Page History'}
															callback={() =>
																navigate(`/sends-admin/history`, {
																	state: { pageIds: pageEntry.id },
																})
															}
															icon={
																<FontAwesomeIcon
																	icon={faRectangleHistory}
																	fixedWidth={true}
																/>
															}
														/>

														<ItemMenuEntry title={'Divider'} sectionDivider={true} />

														<ItemMenuEntry
															title={'Open'}
															callback={() =>
																HandleOpen(pageEntry.liveUrl ? pageEntry.liveUrl : '')
															}
															icon={
																<FontAwesomeIcon
																	icon={faExternalLink}
																	fixedWidth={true}
																/>
															}
														/>

														<ItemMenuEntry
															title={'Copy URL'}
															callback={() =>
																HandleCopyUrl(
																	pageEntry.liveUrl ? pageEntry.liveUrl : ''
																)
															}
															icon={<FontAwesomeIcon icon={faCopy} fixedWidth={true} />}
														/>

														<ItemMenuEntry title={'Divider'} sectionDivider={true} />

														<ItemMenuEntry
															title={'Active'}
															icon={
																<FontAwesomeIcon icon={faWavePulse} fixedWidth={true} />
															}
															callback={() =>
																HandleTemplateStatus(
																	pageEntry.id ? pageEntry.id : -1,
																	!pageEntry.isActive
																)
															}
															addonContent={
																<ToggleSwitch
																	type={ToggleType.Switch}
																	hideLabel={true}
																	label={'Active'}
																	name={`isActive${pageEntry.id}`}
																	checked={pageEntry.isActive}
																/>
															}
														/>

														<ItemMenuEntry
															title={'Delete'}
															callback={() => ShowDeleteConfirmation(pageEntry)}
															icon={<FontAwesomeIcon icon={faTrash} fixedWidth={true} />}
														/>
													</DropdownMenu>
												</div>
											</>
										)}
									</NavLink>
								);
							})
						)}

					{itemCounts.total === 0 && (
						<StackVertical
							gap={FlexGap.ExtraSmall}
							align={FlexAlign.Center}
							justify={FlexJustify.Center}
							extraClass={'m-xxl p-xxl text-center'}
						>
							<FontAwesomeIcon icon={faPaperPlane} size={'3x'} />
							<h6 className={'sub-heading mt-sm mb-xs'}>No Profiles</h6>
							<p>You haven't created any profiles yet</p>
							<Button
								title={'Get Started'}
								colour={ColourMod.Primary}
								onClick={() => navigate('/sends-admin/create')}
							/>
						</StackVertical>
					)}

					{itemCounts.total > 0 && (
						<InfiniteLoadTrigger
							ref={sendsPagesRef.current}
							loaded={itemCounts.loaded}
							totalCount={itemCounts.total}
							hasNextPage={hasNextPage}
							fetchNextPage={fetchNextPage}
							isFetchingNextPage={isFetchingNextPage}
						/>
					)}
				</>
			)}

			{activeSendsPage.isOpen && (
				<ConfirmDeleteSendsPageModal
					pageData={activeSendsPage.pageData}
					isOpen={activeSendsPage.isOpen}
					closeCallback={() => setActiveSendsPage({ ...activeSendsPage, isOpen: false })}
				/>
			)}
		</FullWidthSidebar>
	);
};

export default SendsPagesOverview;
